<template>
  <div :class="attachViaProvider === true ? 'px-4' : 'pa-4'">
    <v-alert v-if="isCnpjInactive" type="error" dense class="mb-4">
      <strong>Edição bloqueada:</strong> CNPJ em situação irregular na Receita Federal. Não é possível realizar alterações.
    </v-alert>
    
    <v-alert v-if="!loadingAddress && address.length === 0" type="warning" dense class="mb-4">
      <div class="d-flex align-center">
        <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
        <strong>Nenhum endereço cadastrado.</strong>&nbsp;É necessário cadastrar pelo menos um endereço para este perfil.
      </div>
    </v-alert>
    
    <div v-if="attachViaProvider != true" class="d-flex align-center mb-4">
      <p class="text-h6 font-weight-bold ma-0">Endereços</p>
      <p class="ma-0 ml-2 text-caption black--text">Estes são os dados que serão incluídos no pedido de inserção/pedido de produção (P.I/P.P)</p>
    </div>

    <v-data-table :headers="headers" :items="address" :items-per-page="5" :loading="loadingAddress" class="elevation-1">
      <template v-slot:[`item.fiscal`]="{ item }">
        <div class="text-center">
          <v-tooltip bottom :disabled="!isCnpjInactive">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-checkbox
                  :input-value="item.main === 1"
                  @click.native="handleFiscalChange(item)"
                  :disabled="isCnpjInactive"
                  readonly
                  hide-details
                  class="mt-0 pt-0"
                  style="display: inline-flex; justify-content: center;"
                ></v-checkbox>
              </span>
            </template>
            <span>CNPJ inativo. Não é possível realizar alterações</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.label`]="{ item }">
        <div class="text-center">{{ item.label || '-' }}</div>
      </template>

      <template v-slot:[`item.street`]="{ item }">
        <div class="text-center">{{ item.street || '-' }}</div>
      </template>

      <template v-slot:[`item.number`]="{ item }">
        <div class="text-center">{{ item.number || '-' }}</div>
      </template>

      <template v-slot:[`item.complement`]="{ item }">
        <div class="text-center">{{ item.complement || '-' }}</div>
      </template>

      <template v-slot:[`item.district`]="{ item }">
        <div class="text-center">{{ item.district || '-' }}</div>
      </template>

      <template v-slot:[`item.zipCode`]="{ item }">
        <div class="text-center">{{ item.zipCode || '-' }}</div>
      </template>

      <template v-slot:[`item.city`]="{ item }">
        <div class="text-center">{{ item.city || '-' }}</div>
      </template>

      <template v-slot:[`item.state`]="{ item }">
        <div class="text-center">{{ item.state || '-' }}</div>
      </template>

      <template v-slot:[`item.edit`]="{ item }">
        <div class="text-center">
          <v-tooltip bottom :disabled="!isCnpjInactive">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" :style="isCnpjInactive ? 'cursor: not-allowed' : 'cursor: pointer'">
                <span :class="isCnpjInactive ? 'subtitle-1 mdi mdi-lead-pencil grey--text' : 'subtitle-1 mdi mdi-lead-pencil'" @click="!isCnpjInactive && openEditAddressModal(item)"></span>
              </span>
            </template>
            <span>CNPJ inativo. Não é possível realizar alterações</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.delete`]="{ item }">
        <div class="text-center">
          <v-tooltip bottom :disabled="!isCnpjInactive">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" :style="isCnpjInactive ? 'cursor: not-allowed' : 'cursor: pointer'">
                <span :style="isCnpjInactive ? 'color: grey' : 'color: red'" class="subtitle-1 mdi mdi-delete" @click="!isCnpjInactive && confirmDeleteAddress(item)"></span>
              </span>
            </template>
            <span>CNPJ inativo. Não é possível realizar alterações</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-tooltip bottom :disabled="!isCnpjInactive">
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" v-on="on">
          <v-btn class="mt-3" @click="openAddAddressModal" color="primary" :disabled="isCnpjInactive">Adicionar Endereço</v-btn>
        </span>
      </template>
      <span>CNPJ inativo. Não é possível realizar alterações</span>
    </v-tooltip>

    <v-dialog v-model="isAddAddress" max-width="700px">
      <v-card class="pa-4">
        <div class="d-flex mb-5 justify-space-between">
          <p class="text-h6 ma-0 font-weight-bold text-uppercase">
            Adicionar de Endereço
          </p>

          <v-icon color="black darken-1" @click="isAddAddress = false">mdi mdi-close</v-icon>
        </div>

        <v-row>
          <v-col class="py-0" cols="6">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Identificação"
              v-model="formAddress.label" outlined dense placeholder="Identificação"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="3">
            <v-select :rules="textRules" required :items="contryList" item-value="id" item-text="text" hide-details
              class="my-2" label="País" v-model="formAddress.country" outlined dense placeholder="Brasil"></v-select>
          </v-col>

          <v-col class="py-0" cols="3">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="CEP"
              v-model="formAddress.zipCode" @input="getZipCodeData()" v-mask="'#####-###'" outlined dense
              placeholder="CEP">
              <template v-slot:append>
                <v-progress-circular v-if="loadingZipCode" class="ml-2" color="primary" size="20" height="7"
                  indeterminate></v-progress-circular>
              </template>
            </v-text-field>
          </v-col>

          <v-col class="py-0" cols="12">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Endereço"
              v-model="formAddress.street" outlined dense placeholder="Endereço" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="6">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Número"
              v-model="formAddress.number" outlined dense placeholder="Número" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="6">
            <v-text-field :rules="textRules" hide-details class="my-2" label="Complemento"
              v-model="formAddress.complement" outlined dense placeholder="Complemento"
              :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="12">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Bairro"
              v-model="formAddress.district" outlined dense placeholder="Bairro" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="8">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Cidade"
              v-model="formAddress.city" outlined dense placeholder="São Paulo" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="4">
            <v-select :rules="textRules" required :items="stateList" item-value="id" item-text="text" hide-details
              class="my-2" label="Estado" v-model="formAddress.state" outlined dense placeholder="Estado"
              :disabled="!isZipCode"></v-select>
          </v-col>
        </v-row>

        <div class="d-flex justify-end mt-3">
          <v-btn @click="submitAssocAddressToVehicle" color="green" dark>Adicionar Endereço

            <v-progress-circular v-if="loadingAssocAddress" class="ml-2" color="white" size="20" height="7"
              indeterminate></v-progress-circular>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isEditAddress" max-width="700px">
      <v-card class="pa-4">
        <div class="d-flex justify-space-between">
          <p class="text-h6 ma-0 font-weight-bold text-uppercase">
            Edição de Endereço
          </p>

          <v-icon color="black darken-1" @click="isEditAddress = false">mdi mdi-close</v-icon>
        </div>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col class="py-0" cols="6">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Identificação"
              v-model="formAddress.label" outlined dense placeholder="Identificação"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="3">
            <v-select :rules="textRules" required :items="contryList" item-value="id" item-text="text" hide-details
              class="my-2" label="País" v-model="formAddress.country" outlined dense placeholder="Brasil"></v-select>
          </v-col>

          <v-col class="py-0" cols="3">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="CEP"
              v-model="formAddress.zipCode" @input="getZipCodeData()" v-mask="'#####-###'" outlined dense
              placeholder="CEP"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="12">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Endereço"
              v-model="formAddress.street" outlined dense placeholder="Endereço" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="6">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Número"
              v-model="formAddress.number" outlined dense placeholder="Número" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="6">
            <v-text-field hide-details class="my-2" label="Complemento" v-model="formAddress.complement" outlined dense
              placeholder="Complemento" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="12">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Bairro"
              v-model="formAddress.district" outlined dense placeholder="Bairro" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="8">
            <v-text-field :rules="textRules" required hide-details class="my-2" label="Cidade"
              v-model="formAddress.city" outlined dense placeholder="São Paulo" :disabled="!isZipCode"></v-text-field>
          </v-col>

          <v-col class="py-0" cols="4">
            <v-select :rules="textRules" required :items="stateList" item-value="id" item-text="text" hide-details
              class="my-2" label="Estado" v-model="formAddress.state" outlined dense placeholder="Estado"
              :disabled="!isZipCode"></v-select>
          </v-col>
        </v-row>

        <div class="d-flex justify-end mt-3">
          <v-btn @click="submitEditAddress" color="green" dark>Salvar

            <v-progress-circular v-if="loadingEditAddress" class="ml-2" color="white" size="20" height="7"
              indeterminate></v-progress-circular>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteConfirmation" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          Confirmar Exclusão
        </v-card-title>
        <v-card-text>
          Você tem certeza que deseja deletar o endereço "{{ addressToDelete?.label }}"?
          <br><br>
          Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="isDeleteConfirmation = false">
            Cancelar
          </v-btn>
          <v-btn color="red" dark @click="confirmDeleteAddressAction">
            Deletar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isFiscalConfirmation" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          Alterar Endereço Fiscal
        </v-card-title>
        <v-card-text>
          Deseja definir o endereço "{{ addressToChangeFiscal?.label }}" como o endereço fiscal?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="isFiscalConfirmation = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" dark @click="confirmChangeFiscalAddress">
            Confirmar
            <v-progress-circular v-if="loadingChangeFiscal" class="ml-2" color="white" size="20" height="7"
              indeterminate></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="deleteErrorSnackBar" color="red">
      Veículo deve estar associado a pelo menos um endereço.
    </v-snackbar>

    <v-snackbar v-model="toastSuccess" :timeout="4000" color="success" top>
      {{ textToastSuccess }}
    </v-snackbar>

    <v-snackbar v-model="toastError" :timeout="4000" color="red" top>
      {{ textToastError }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
export default {
  name: "AddressProfile",

  props: {
    profileId: {
      type: [String, Number],
      required: true,
    },
    attachViaProvider: {
      type: Boolean,
      default: false
    },
    isCnpjInactive: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    this.getAddress();
    
  },

  data() {
    return {
      textToastSuccess: "",
      toastSuccess: false,
      textToastError: "",
      toastError: false,
      loadingEditAddress: false,
      isAddAddress: false,
      isEditAddress: false,
      isZipCode: true,
      loadingAddress: false,
      deleteErrorSnackBar: false,
      loadingAssocAddress: false,
      loadingZipCode: false,
      isDeleteConfirmation: false,
      addressToDelete: null,
      isFiscalConfirmation: false,
      addressToChangeFiscal: null,
      loadingChangeFiscal: false,

      idEditAddress: null,

      textRules: [(v) => !!v || "Campo Obrigatório"],

      formAddress: {
        label: "",
        country: "",
        city: "",
        zipCode: "",
        street: "",
        district: "",
        number: "",
        state: "",
        complement: "",
      },

      contryList: [
        {
          id: "BRA",
          text: "Brasil",
        },
      ],

      headers: [
        {
          text: "Fiscal",
          value: "fiscal",
          align: "center",
        },
        {
          text: "Identificação",
          value: "label",
          align: "center",
        },
        {
          text: "Endereço",
          value: "street",
          align: "center",
        },
        {
          text: "Número",
          value: "number",
          align: "center",
        },
        {
          text: "Complemento",
          value: "complement",
          align: "center",
        },
        {
          text: "Bairro",
          value: "district",
          align: "center",
        },
        {
          text: "CEP",
          value: "zipCode",
          align: "center",
        },
        {
          text: "Cidade",
          value: "city",
          align: "center",
        },
        {
          text: "Estado",
          value: "state",
          align: "center",
        },
        {
          text: "",
          value: "edit",
          align: "center",
        },
        {
          text: "",
          value: "delete",
          align: "center",
        },
      ],

      stateList: [
        {
          id: "AC",
          text: "Acre",
        },
        {
          id: "AL",
          text: "Alagoas",
        },
        {
          id: "AP",
          text: "Amapá",
        },
        {
          id: "AM",
          text: "Amazonas",
        },
        {
          id: "BA",
          text: "Bahia",
        },
        {
          id: "CE",
          text: "Ceará",
        },
        {
          id: "DF",
          text: "Distrito Federal",
        },
        {
          id: "ES",
          text: "Espírito Santo",
        },
        {
          id: "GO",
          text: "Goiás",
        },
        {
          id: "MA",
          text: "Maranhão",
        },
        {
          id: "MT",
          text: "Mato Grosso",
        },
        {
          id: "MS",
          text: "Mato Grosso do Sul",
        },
        {
          id: "MG",
          text: "Minas Gerais",
        },
        {
          id: "PA",
          text: "Pará",
        },
        {
          id: "PB",
          text: "Paraíba",
        },
        {
          id: "PR",
          text: "Paraná",
        },
        {
          id: "PE",
          text: "Pernambuco",
        },
        {
          id: "PI",
          text: "Piauí",
        },
        {
          text: "Rio de Janeiro",
        },
        {
          id: "RN",
          text: "Rio Grande do Norte",
        },
        {
          id: "RS",
          text: "Rio Grande do Sul",
        },
        {
          id: "RO",
          text: "Rondônia",
        },
        {
          id: "RR",
          text: "Roraima",
        },
        {
          id: "SC",
          text: "Santa Catarina",
        },
        {
          id: "SP",
          text: "São Paulo",
        },
        {
          id: "SE",
          text: "Sergipe",
        },
        {
          id: "TO",
          text: "Tocantins",
        },
      ],

      address: [],
    };
  },

  methods: {
    showToastSuccess(msg) {
      this.textToastSuccess = `${msg}`;
      this.toastSuccess = true;
    },

    showToastError(msg) {
      this.textToastError = `${msg}`;
      this.toastError = true;
    },

    async getAddress() {
      try {
        this.loadingAddress = true;
        this.address = [];

        const response = await this.$axios.get(`address/admin/profile/${this.profileId}`);

        if (response.data && response.data.data) {
          const profileAddress = response.data.data;
          profileAddress.forEach((address) => {
            if (address.statusDc === "Ativo") {
              this.address.push(address);
            }
          });
        }
      } catch (error) {
        console.error('Erro ao carregar endereços:', error);
        this.showToastError("Erro ao carregar endereços!");
      } finally {
        this.loadingAddress = false;
      }
    },

    openAddAddressModal() {
      this.isAddAddress = true;
      this.resetFormAddress();
    },

    openEditAddressModal(address) {
      this.isEditAddress = true;
      this.idEditAddress = address.id;

      let countryFormatted = address.country === "Brasil" ? "BRA" : "BRA";

      this.formAddress = {
        ...this.formAddress,
        label: address.label,
        zipCode: address.zipCode,
        country: countryFormatted,
        street: address.street,
        city: address.city,
        district: address.district,
        number: address.number,
        state: address.state,
        complement: address.complement,
      };
    },

    resetFormAddress() {
      this.formAddress.label = "";
      this.formAddress.country = "";
      this.formAddress.city = "";
      this.formAddress.zipCode = "";
      this.formAddress.street = "";
      this.formAddress.district = "";
      this.formAddress.number = "";
      this.formAddress.state = "";
      this.formAddress.complement = "";
    },

    async submitEditAddress() {
      try {
        this.loadingEditAddress = true;
        await this.$axios.put(`address/${this.idEditAddress}`, {
          label: this.formAddress.label,
          country: this.formAddress.country,
          city: this.formAddress.city,
          zipCode: this.formAddress.zipCode,
          street: this.formAddress.street,
          district: this.formAddress.district,
          number: this.formAddress.number,
          state: this.formAddress.state,
          complement: this.formAddress.complement,
        });

        this.getAddress();
        this.isEditAddress = false;
        this.showToastSuccess("Endereço editado!");
      } catch (error) {
        console.error('Erro ao editar endereço:', error);
        this.showToastError("Ops, algum erro ocorreu!");
      } finally {
        this.loadingEditAddress = false;
      }
    },

    async submitAssocAddressToVehicle() {
      try {
        this.loadingAssocAddress = true;
        await this.$axios.post(`address`, {
          profileId: this.profileId,
          label: this.formAddress.label,
          country: this.formAddress.country,
          city: this.formAddress.city,
          zipCode: this.formAddress.zipCode,
          street: this.formAddress.street,
          district: this.formAddress.district,
          number: this.formAddress.number,
          state: this.formAddress.state,
          complement: this.formAddress.complement,
        });

        this.getAddress();
        this.isAddAddress = false;
        this.showToastSuccess("Endereço adicionado!");
      } catch (error) {
        console.error('Erro ao adicionar endereço:', error);
        this.showToastError("Ops, algum erro ocorreu!");
      } finally {
        this.loadingAssocAddress = false;
      }
    },

    confirmDeleteAddress(address) {
      this.addressToDelete = address;
      this.isDeleteConfirmation = true;
    },

    async confirmDeleteAddressAction() {
      if (this.address.length > 1) {
        try {
          this.loadingAddress = true;
          await this.$axios.delete(`address/${this.addressToDelete.id}`);

          this.getAddress();
          this.showToastSuccess("Endereço deletado!");
          this.isDeleteConfirmation = false;
          this.addressToDelete = null;
        } catch (error) {
          console.error('Erro ao deletar endereço:', error);
          this.showToastError("Erro ao deletar endereço!");
          this.isDeleteConfirmation = false;
        }
      } else {
        this.deleteErrorSnackBar = true;
        this.isDeleteConfirmation = false;
      }
    },

    async getZipCodeData() {
      const cep = this.formAddress.zipCode.replace(/\D/g, '');
      
      if (cep.length === 8) {
        try {
          this.loadingZipCode = true;
          this.isZipCode = false;
          
          const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
          
          if (response.data && !response.data.erro) {
            const data = response.data;
            
            this.formAddress.street = data.logradouro || '';
            this.formAddress.district = data.bairro || '';
            this.formAddress.city = data.localidade || '';
            this.formAddress.state = data.uf || '';
            this.formAddress.complement = data.complemento || '';
            this.formAddress.country = 'BRA';
            
            this.isZipCode = true;
          } else {
            this.showToastError('CEP não encontrado!');
            this.isZipCode = true;
          }
        } catch (error) {
          console.error('Erro ao buscar CEP:', error);
          this.showToastError('Erro ao buscar CEP!');
          this.isZipCode = true;
        } finally {
          this.loadingZipCode = false;
        }
      } else {
        this.formAddress.street = '';
        this.formAddress.district = '';
        this.formAddress.city = '';
        this.formAddress.state = '';
        this.formAddress.complement = '';
        this.isZipCode = false;
      }
    },

    handleFiscalChange(address) {
      if (address.main === 1) {
        return;
      }
      
      this.addressToChangeFiscal = address;
      this.isFiscalConfirmation = true;
    },

    async confirmChangeFiscalAddress() {
      try {
        this.loadingChangeFiscal = true;
        
        const currentMainAddress = this.address.find(addr => addr.main === 1);
        
        if (currentMainAddress) {
          await this.$axios.put(`/address/${currentMainAddress.id}`, {
            label: currentMainAddress.label,
            country: currentMainAddress.country,
            city: currentMainAddress.city,
            zipCode: currentMainAddress.zipCode,
            street: currentMainAddress.street,
            district: currentMainAddress.district,
            number: currentMainAddress.number,
            state: currentMainAddress.state,
            complement: currentMainAddress.complement,
            main: 0
          });
        }
        
        await this.$axios.put(`/address/${this.addressToChangeFiscal.id}`, {
          label: this.addressToChangeFiscal.label,
          country: this.addressToChangeFiscal.country,
          city: this.addressToChangeFiscal.city,
          zipCode: this.addressToChangeFiscal.zipCode,
          street: this.addressToChangeFiscal.street,
          district: this.addressToChangeFiscal.district,
          number: this.addressToChangeFiscal.number,
          state: this.addressToChangeFiscal.state,
          complement: this.addressToChangeFiscal.complement,
          main: 1
        });
        
        this.showToastSuccess('Endereço fiscal principal atualizado com sucesso!');
        this.isFiscalConfirmation = false;
        this.addressToChangeFiscal = null;
        
        await this.getAddress();
      } catch (error) {
        console.error('Erro ao alterar endereço fiscal:', error);
        this.showToastError('Erro ao alterar endereço fiscal!');
      } finally {
        this.loadingChangeFiscal = false;
      }
    },

  },
};
</script>

<style lang="scss" scoped>
* {
  font-family: 'Roboto', sans-serif !important;
}
</style>